import type { AssistantReply, ChatAction } from '@/types'

import { directionsUrl, whatsappUrl } from '@/lib/business'
import { services } from '@/constants/services'

import {
    address,
    contact,
    hours,
    siteConfig,
    whatsappMessages
} from '@/constants/site'

// Built-in assistant: answers the questions visitors ask most, instantly and for free.
// The chat endpoint uses it on its own, or alongside an AI model when a key is configured.

type IntentId =
    | 'price'
    | 'booking'
    | 'hours'
    | 'location'
    | 'contact'
    | 'bridal'
    | 'makeover'
    | 'nails'
    | 'skin'
    | 'facial'
    | 'hair'
    | 'products'
    | 'women'
    | 'instagram'
    | 'services'
    | 'artists'
    | 'thanks'
    | 'bye'
    | 'greeting'

type Intent = {
    id: IntentId
    weight: number
    keywords: string[]
}

// Order matters: when two intents score the same, the earlier one wins.
// A keyword ending in * matches any word that starts with it.
const INTENTS: Intent[] = [
    {
        id: 'price',
        weight: 1.4,
        keywords: ['price*', 'rate', 'rates', 'cost*', 'charge*', 'fee', 'fees', 'kitna', 'kitne', 'kitni', 'paisa', 'paise', 'budget', 'package*', 'offers', 'discount*', 'rupee*', 'rs', 'inr', 'expensive', 'cheap', 'affordable', 'कीमत', 'रेट', 'कितना', 'कितने', 'चार्ज*']
    },
    {
        id: 'booking',
        weight: 1.2,
        keywords: ['book*', 'appointment*', 'appoint*', 'slot*', 'reserve*', 'reservation*', 'schedule*', 'availab*', 'walk in', 'walkin', 'बुक*', 'अपॉइंटमेंट']
    },
    {
        id: 'hours',
        weight: 1.2,
        keywords: ['time', 'timing*', 'hour*', 'open*', 'closing', 'closed', 'closes', 'khula', 'khulta', 'khulti', 'khulega', 'band', 'kab', 'today', 'tomorrow', 'sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'holiday*', 'weekend*', 'samay', 'समय', 'टाइम', 'खुला', 'बंद']
    },
    {
        id: 'location',
        weight: 1.1,
        keywords: ['address', 'location*', 'locat*', 'where', 'kaha', 'kahan', 'kidhar', 'map', 'maps', 'direction*', 'route', 'reach', 'rasta', 'pata', 'metro', 'landmark', 'nearby', 'near', 'distance', 'parking', 'model town', 'gtb', 'vijay nagar', 'kamla nagar', 'hudson', 'पता', 'कहां', 'कहाँ']
    },
    {
        id: 'contact',
        weight: 1.1,
        keywords: ['phone', 'number', 'call*', 'contact*', 'mobile', 'whatsapp*', 'whats app', 'message', 'msg', 'नंबर', 'फोन']
    },
    {
        id: 'bridal',
        weight: 1.5,
        keywords: ['bridal', 'bride*', 'dulhan', 'shaadi', 'shadi', 'wedding*', 'marriage', 'vivah', 'engagement', 'roka', 'reception', 'airbrush', 'hd', 'pheras', 'pre bridal', 'prebridal', 'दुल्हन', 'शादी', 'ब्राइडल']
    },
    {
        id: 'makeover',
        weight: 1.3,
        keywords: ['makeover*', 'party', 'parties', 'function*', 'event*', 'sangeet', 'mehendi', 'mehndi', 'haldi', 'festival*', 'diwali', 'karwa', 'teej', 'farewell', 'birthday', 'anniversary', 'draping', 'drape*', 'saree', 'sari', 'lehenga', 'dupatta', 'glam*', 'makeup', 'make up', 'mekup', 'मेकअप']
    },
    {
        id: 'nails',
        weight: 1.4,
        keywords: ['nail*', 'manicure*', 'pedicure*', 'extension*', 'gel', 'french', 'acrylic', 'nakhun', 'nakhoon', 'नाखून', 'नेल*']
    },
    {
        id: 'skin',
        weight: 1.3,
        keywords: ['skin*', 'acne', 'pimple*', 'tan', 'tanning', 'pigment*', 'glow*', 'dull*', 'organic', 'herbal', 'spots', 'dryness', 'oily', 'sensitive', 'त्वचा', 'स्किन']
    },
    {
        id: 'facial',
        weight: 1.4,
        keywords: ['facial*', 'cleanup*', 'clean up', 'bleach*', 'detan', 'de tan', 'blackhead*', 'whitehead*', 'massage', 'threading', 'wax*', 'फेशियल']
    },
    {
        id: 'hair',
        weight: 1.4,
        keywords: ['hair*', 'haircut*', 'kerat*', 'kertain', 'kertaing', 'keretin', 'smoothen*', 'smoothning', 'straighten*', 'rebond*', 'spa', 'frizz*', 'baal', 'braid*', 'bun', 'buns', 'curl*', 'blow dry', 'blowdry', 'updo*', 'बाल', 'हेयर']
    },
    {
        id: 'products',
        weight: 1,
        keywords: ['product*', 'brand*', 'quality', 'chemical*', 'safe', 'hygien*']
    },
    {
        id: 'women',
        weight: 1.2,
        keywords: ['men', 'male', 'gents', 'boy', 'boys', 'guy', 'guys', 'man', 'couple*', 'husband', 'unisex', 'ladies', 'lady', 'women', 'womens', 'female', 'girls']
    },
    {
        id: 'instagram',
        weight: 1.2,
        keywords: ['insta*', 'photo*', 'pic', 'pics', 'picture*', 'portfolio', 'work', 'gallery', 'reel*', 'video*', 'sample*']
    },
    {
        id: 'services',
        weight: 1,
        keywords: ['service*', 'offer', 'menu', 'list', 'facilit*', 'provide', 'treatments', 'kya kya', 'सेवा*']
    },
    {
        id: 'artists',
        weight: 1,
        keywords: ['artist*', 'expert*', 'experienc*', 'professional*', 'staff', 'beautician*', 'trained', 'qualified']
    },
    {
        id: 'thanks',
        weight: 0.6,
        keywords: ['thank*', 'thanku', 'thnx', 'thx', 'shukriya', 'dhanyavad', 'dhanyawad', 'धन्यवाद', 'शुक्रिया']
    },
    {
        id: 'bye',
        weight: 0.5,
        keywords: ['bye', 'goodbye', 'good bye', 'see you', 'tata', 'alvida']
    },
    {
        id: 'greeting',
        weight: 0.4,
        keywords: ['hi', 'hii*', 'hello*', 'helo', 'hey*', 'namaste', 'namaskar', 'good morning', 'good afternoon', 'good evening', 'hola', 'नमस्ते', 'नमस्कार']
    }
]

const SERVICE_INTENTS: Partial<Record<IntentId, string>> = {
    bridal: 'bridal-makeup',
    makeover: 'party-makeovers',
    nails: 'nail-art',
    skin: 'skin-treatments',
    facial: 'facial-cleanup',
    hair: 'hair-care'
}

const ADD_ON_INTENTS: IntentId[] = ['price', 'booking', 'hours', 'location', 'contact']

export const DEFAULT_SUGGESTIONS = [
    'Show all services',
    'Bridal makeup',
    'Timings',
    'Location',
    'Book an appointment'
]

const SERVICE_SUGGESTIONS = [
    'Bridal makeup',
    'Party makeover',
    'Nail art',
    'Skin treatment',
    'Facial cleanup',
    'Hair care'
]

export const ASSISTANT_GREETING: AssistantReply = {
    text: `Namaste! Welcome to ${siteConfig.name}, a women's beauty parlour in New Gupta Colony, Delhi. I can help you with our services, timings, location and bookings. What would you like to know?`,
    suggestions: DEFAULT_SUGGESTIONS
}

const action = {
    whatsapp: (message?: string, label: string = 'Chat on WhatsApp'): ChatAction => ({ kind: 'whatsapp', label, href: whatsappUrl(message) }),
    call: (): ChatAction => ({ kind: 'call', label: `Call ${contact.phoneShort}`, href: contact.phoneHref }),
    directions: (): ChatAction => ({ kind: 'directions', label: 'Get directions', href: directionsUrl() }),
    instagram: (): ChatAction => ({ kind: 'instagram', label: 'Open Instagram', href: contact.instagramUrl }),
    page: (slug: string, label: string): ChatAction => ({ kind: 'link', label, href: `/services/${slug}` })
}

const normalize = (text: string): string => {
    return ` ${text.toLowerCase().replace(/[^a-z0-9ऀ-ॿ]+/g, ' ').trim()} `
}

const hasKeyword = (normalized: string, keyword: string): boolean => {
    if (keyword.endsWith('*')) return normalized.includes(` ${keyword.slice(0, -1)}`)

    return normalized.includes(` ${keyword} `)
}

const scoreIntents = (message: string): IntentId[] => {
    const normalized = normalize(message)

    return INTENTS
        .map((intent, index) => ({
            id: intent.id,
            index,
            score: intent.keywords.filter(keyword => hasKeyword(normalized, keyword)).length * intent.weight
        }))
        .filter(result => result.score > 0)
        .sort((a, b) => b.score - a.score || a.index - b.index)
        .map(result => result.id)
}

const serviceFor = (intent: IntentId) => {
    const slug = SERVICE_INTENTS[intent]

    return slug ? services.find(service => service.slug === slug) : undefined
}

const serviceReply = (intent: IntentId): AssistantReply | null => {
    const service = serviceFor(intent)

    if (!service) return null

    const texts: Partial<Record<IntentId, string>> = {
        bridal: "Yes, we do bridal makeups! Choose HD makeup for a natural, skin-like finish or airbrush makeup for a flawless, soft-focus base. We also do bridal hairstyles, dupatta setting, draping, pre-bridal skin care and looks for engagement, mehendi, sangeet and reception. Share your wedding date on WhatsApp to reserve your slot.",
        makeover: "Our makeovers are perfect for parties, engagements, sangeet, festivals and every celebration. Choose soft and natural or full glam, with eye makeup, lashes, a matching hairstyle and neat saree, lehenga or dupatta draping.",
        nails: "Our experienced nail artist does designer and bridal nail art, nail extensions, gel polish and French tips, plus nail care like cleaning, shaping and cuticle care. Have a design in mind? Send us a picture on WhatsApp.",
        skin: "We care about your skin, so every treatment starts with understanding your skin type. We offer de-tan and brightening care, hydration for dry or dull skin, organic and herbal options, oil control care and pre-bridal glow treatments.",
        facial: "Our facial cleanups deep-cleanse your pores, clear blackheads and finish with a soothing mask and massage. We also offer glow, fruit and gold facials, de-tan and bleach, along with threading and waxing.",
        hair: "For hair care we offer hair spa, keratin treatment, smoothening, straightening, rebonding, haircuts and blow-dry styling. For weddings and parties, we create buns, braids with flowers, curls, waves and updos."
    }

    return {
        text: texts[intent] ?? service.short,
        actions: [
            action.whatsapp(service.whatsappMessage, 'Ask on WhatsApp'),
            action.page(service.slug, `${service.name} details`)
        ],
        suggestions: ['Book an appointment', 'Timings', 'Location', 'Show all services']
    }
}

const baseReply = (intent: IntentId, serviceName?: string): AssistantReply => {
    switch (intent) {
        case 'price':
            return {
                text: `Our charges${serviceName ? ` for ${serviceName.toLowerCase()}` : ''} depend on the service and the look you choose, so we share them personally. Send us your requirement on WhatsApp or give us a call and we will guide you.`,
                actions: [
                    action.whatsapp(`${whatsappMessages.charges}${serviceName ?? 'your services'}.`, 'Ask charges on WhatsApp'),
                    action.call()
                ],
                suggestions: ['Show all services', 'Book an appointment', 'Location']
            }
        case 'booking':
            return {
                text: `Booking is easy! Send us a WhatsApp message with ${serviceName ? `"${serviceName}"` : 'the service you want'} and your preferred date and time, and we will confirm your slot. You can also call us on ${contact.phoneDisplay}.`,
                actions: [
                    action.whatsapp(serviceName ? `Hello Anushka Beauty Parlour, I would like to book ${serviceName}.` : whatsappMessages.general, 'Book on WhatsApp'),
                    action.call()
                ],
                suggestions: ['Timings', 'Location', 'Show all services']
            }
        case 'hours':
            return {
                text: `We are ${hours.days.toLowerCase()}, from ${hours.label}. Booking ahead on WhatsApp helps us keep your preferred time ready.`,
                actions: [action.whatsapp(whatsappMessages.general, 'Book on WhatsApp'), action.call()],
                suggestions: ['Location', 'Book an appointment', 'Show all services']
            }
        case 'location':
            return {
                text: `You will find us at ${address.full}. The nearest metro station is ${address.nearestMetro}. Tap "Get directions" for the route from your current location.`,
                actions: [action.directions(), action.call()],
                suggestions: ['Timings', 'Book an appointment', 'Contact number']
            }
        case 'contact':
            return {
                text: `You can call or WhatsApp us on ${contact.phoneDisplay}, every day from ${hours.label}.`,
                actions: [action.call(), action.whatsapp()],
                suggestions: ['Location', 'Timings', 'Show all services']
            }
        case 'products':
            return {
                text: "We use only high quality products for every service, with organic and herbal options for sensitive skin. The health of your skin always comes first for us.",
                actions: [action.whatsapp()],
                suggestions: ['Skin treatment', 'Facial cleanup', 'Show all services']
            }
        case 'women':
            return {
                text: `${siteConfig.name} is a women-only parlour, so you can relax in a comfortable and private space.`,
                actions: [action.whatsapp()],
                suggestions: ['Show all services', 'Timings', 'Location']
            }
        case 'instagram':
            return {
                text: `You can see our makeups, nail arts and hair designs on our Instagram profile, ${contact.instagramHandle}.`,
                actions: [action.instagram(), action.whatsapp()],
                suggestions: ['Bridal makeup', 'Nail art', 'Book an appointment']
            }
        case 'services':
            return {
                text: [
                    "We offer all kinds of beauty facilities:",
                    "• Makeovers and party makeup with draping",
                    "• Bridal makeups (HD and airbrush)",
                    "• Skin treatments, including organic options",
                    "• Nail art, nail extensions and nail treatment",
                    "• Facial cleanup, facials and de-tan",
                    "• Hair care, keratin and hair designs",
                    "We also do threading, waxing, manicure and pedicure. Which one would you like to know more about?"
                ].join('\n'),
                suggestions: SERVICE_SUGGESTIONS
            }
        case 'artists':
            return {
                text: "Experienced professional make-up artists and nail artists are available at our parlour. Every look is planned around your face, your skin and your outfit.",
                actions: [action.instagram(), action.whatsapp()],
                suggestions: ['Bridal makeup', 'Nail art', 'Book an appointment']
            }
        case 'thanks':
            return {
                text: "You are most welcome! If you have any other question, I am right here. You can also reach us on WhatsApp anytime.",
                suggestions: DEFAULT_SUGGESTIONS
            }
        case 'bye':
            return {
                text: `Thank you for visiting ${siteConfig.name}. We look forward to making you look and feel beautiful!`,
                actions: [action.whatsapp(whatsappMessages.general, 'Book on WhatsApp')]
            }
        case 'greeting':
            return {
                text: "Namaste! How can I help you today? Ask me about our services, timings, location or booking.",
                suggestions: DEFAULT_SUGGESTIONS
            }
        default:
            return serviceReply(intent) ?? fallbackReply()
    }
}

const addOnText = (intent: IntentId): string | null => {
    switch (intent) {
        case 'price':
            return "Charges depend on the look and services you choose, so message us on WhatsApp for the details."
        case 'booking':
            return "To book, send us a WhatsApp message with your preferred date and time."
        case 'hours':
            return `We are ${hours.days.toLowerCase()}, ${hours.label}.`
        case 'location':
            return `Find us at ${address.lineOne}, ${address.lineTwo}.`
        case 'contact':
            return `Call or WhatsApp us on ${contact.phoneDisplay}.`
        default:
            return null
    }
}

const addOnAction = (intent: IntentId): ChatAction | null => {
    if (intent === 'location') return action.directions()
    if (intent === 'contact' || intent === 'price') return action.call()

    return null
}

const fallbackReply = (): AssistantReply => {
    return {
        text: `I am not sure about that one, but our team will be happy to help. Message us on WhatsApp or call ${contact.phoneDisplay}, or pick a topic below.`,
        actions: [action.whatsapp(), action.call()],
        suggestions: DEFAULT_SUGGESTIONS
    }
}

export const getAssistantReply = (message: string): AssistantReply => {
    const intents = scoreIntents(message)

    if (!message.trim()) return ASSISTANT_GREETING
    if (intents.length === 0) return fallbackReply()

    const [primary, ...others] = intents
    const matchedService = intents.map(serviceFor).find(Boolean)
    const isMetaIntent = ADD_ON_INTENTS.includes(primary)

    const reply = isMetaIntent && (primary === 'price' || primary === 'booking')
        ? baseReply(primary, matchedService?.name)
        : baseReply(primary)

    const addOns = others
        .filter(intent => ADD_ON_INTENTS.includes(intent))
        .slice(0, 2)

    const extraText = addOns.map(addOnText).filter(Boolean).join(' ')
    const extraActions = addOns.map(addOnAction).filter((item): item is ChatAction => Boolean(item))

    const actions = [...(reply.actions ?? []), ...extraActions]
        .filter((item, index, list) => list.findIndex(other => other.href === item.href) === index)
        .slice(0, 3)

    if (isMetaIntent && matchedService && primary !== 'price' && primary !== 'booking') {
        actions.push(action.page(matchedService.slug, `${matchedService.name} details`))
    }

    return {
        ...reply,
        text: extraText ? `${reply.text}\n\n${extraText}` : reply.text,
        actions: actions.length > 0 ? actions.slice(0, 3) : undefined
    }
}
